/**
 * @file Plan.tsx
 * @description Component for displaying the editorial plan and its generated contents.
 */

import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { toast } from "react-toastify";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

import config from "../../config/config";
import { auth } from "../../config/firebaseConfig";
import log from "../../config/logging";

import Loading from "../../components/common/Loading";
import ContentItem from "../../components/common/ContentItem";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { PlanDetails, GeneratedContentDetails } from "../../types";
import { updatePlanName } from "../../services/planService";

const Plan: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const [planDetails, setPlanDetails] = useState<PlanDetails | null>(null);
  const [contentDetails, setContentDetails] = useState<
    GeneratedContentDetails[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editingTitle, setEditingTitle] = useState("");
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadStatusChange = (id: string, downloaded: boolean) => {
    setContentDetails((prevDetails) =>
      prevDetails.map((detail) =>
        detail.id === id ? { ...detail, downloaded } : detail
      )
    );
  };

  const handleStartEditingTitle = () => {
    if (planDetails?.name) {
      setEditingTitle(planDetails.name);
      setIsEditingTitle(true);
      // Focus input after state update
      setTimeout(() => {
        titleInputRef.current?.focus();
        titleInputRef.current?.select();
      }, 0);
    }
  };

  const handleCancelEditingTitle = () => {
    setIsEditingTitle(false);
    setEditingTitle("");
  };

  const handleSaveTitle = async () => {
    if (!id || !planDetails) return;

    const trimmedTitle = editingTitle.trim();
    if (!trimmedTitle) {
      toast.error("Il titolo non può essere vuoto");
      return;
    }

    if (trimmedTitle === planDetails.name) {
      setIsEditingTitle(false);
      return;
    }

    try {
      setIsSavingTitle(true);
      await updatePlanName(id, trimmedTitle);
      setPlanDetails({ ...planDetails, name: trimmedTitle });
      setIsEditingTitle(false);
      toast.success("Titolo aggiornato con successo");
      log.info("Plan title updated successfully", { planId: id, newTitle: trimmedTitle });
    } catch (error) {
      log.error("Error updating plan title", { error, planId: id });
      toast.error("Errore durante l'aggiornamento del titolo");
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveTitle();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancelEditingTitle();
    }
  };

  useEffect(() => {
    const fetchPlanDetails = async () => {
      if (loading) return;

      if (!user) {
        log.warn("User not authenticated, redirecting to login");
        toast.error("Per favore, accedi per visualizzare questa pagina");
        navigate("/accedi");
        return;
      }

      try {
        const token = await user.getIdToken(true);
        const response = await fetch(`${config.backendUrl}/api/plans/${id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Errore: ${response.statusText}`);
        }

        const data = await response.json();
        log.debug("Plan details fetched successfully", { data });

        setPlanDetails(data.plan);
        setContentDetails(data.plan.contents || []);
        log.debug("Content details set", {
          contentDetails: data.plan.contents,
        });
      } catch (error: unknown) {
        log.error("Error fetching plan details", { error });
        toast.error("Errore durante il recupero del piano editoriale");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlanDetails();
  }, [id, user, loading, navigate]);

  if (loading || isLoading) {
    return <Loading />;
  }

  log.debug("Rendering Plan component", { planDetails, contentDetails });

  return (
    <section className="mx-auto max-w-content">
      <h2 className="!text-5xl !font-normal text-primary-content !mb-20 flex items-center gap-6">
        <ArticleOutlinedIcon className="text-5xl" />
        Ecco i contenuti del tuo piano editoriale
      </h2>

      <div className="flex flex-row items-start gap-8">
        <aside className="flex flex-col items-center w-1/3 gap-4 p-6 xl:p-12 bg-base-100 rounded-tl-6xl rounded-br-6xl">
          <h4 className="text-3xl font-medium text-center text-primary">
            Vuoi migliorare il risultato?
          </h4>
          <Link
            to={`/genera-piano/${id}`}
            className="px-6 my-1 text-lg font-medium border-none rounded-full outline-none text-primary-content bg-accent btn flex-nowrap whitespace-nowrap btn-block"
          >
            <ArticleOutlinedIcon className="w-4" />
            Rigenera
          </Link>
        </aside>
        <section className="flex flex-col gap-4 xl:p-12 bg-base-100 rounded-tr-6xl rounded-bl-6xl">
          <div className="flex items-center gap-3 group">
            {isEditingTitle ? (
              <div className="flex items-center gap-2 flex-1">
                <input
                  ref={titleInputRef}
                  type="text"
                  value={editingTitle}
                  onChange={(e) => setEditingTitle(e.target.value)}
                  onKeyDown={handleTitleKeyDown}
                  disabled={isSavingTitle}
                  className="flex-1 !text-4xl !font-medium !mb-0 rounded-2xl border border-gray-300 input input-lg"
                  placeholder="Inserisci il titolo del piano editoriale"
                />
                <button
                  onClick={handleSaveTitle}
                  disabled={isSavingTitle}
                  className="p-2 rounded-full btn btn-primary btn-sm"
                  title="Salva"
                >
                  <CheckIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={handleCancelEditingTitle}
                  disabled={isSavingTitle}
                  className="p-2 rounded-full btn btn-outline btn-sm"
                  title="Annulla"
                >
                  <CloseIcon className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <>
                <p className="!text-4xl !font-medium !mb-0 flex-1">{planDetails?.name}</p>
                <button
                  onClick={handleStartEditingTitle}
                  className="p-2 rounded-full btn btn-ghost btn-sm opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Modifica titolo"
                >
                  <EditOutlinedIcon className="w-5 h-5 text-primary" />
                </button>
              </>
            )}
          </div>
          <p className="!mb-8 text-lg">{planDetails?.description}</p>
          <ul className="flex flex-col gap-4">
            {contentDetails.map((contentDetail) => (
              <li
                key={contentDetail.id}
                className="flex items-start justify-between w-full gap-8 p-8 mb-4 border xl:gap-12 rounded-3xl bg-base-100 border-gray-line"
              >
                <ContentItem
                  contentDetail={contentDetail}
                  onDownloadStatusChange={handleDownloadStatusChange}
                />
              </li>
            ))}
          </ul>
        </section>
      </div>
    </section>
  );
};

export default Plan;
